import React, { useState } from "react";
import axios from "axios";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import StepShell from "./StepShell";
import { NACHA_MANDATE_TEXT } from "./StripeAchSetup";
import { errorMessage } from "./apiError";
import { textButton, tileButton } from "./ui";

export function methodLabel(m) {
  if (!m) return null;
  return m.payment_method_type === "ach"
    ? `${m.bank_name || "Bank"} ending in ${m.last4 || "••••"}`
    : `Card ending in ${m.last4 || "••••"}`;
}

/*
 * Saves a card or bank without reserving anything: /setup with vault_only,
 * Stripe's own confirm, then /payment-method/sync for what was saved (the
 * webhook can land after the page is already done).
 */
export default function PaymentStep({
  tokenUrl,
  truckerName,
  saved,
  freeLoad,
  notice = "",
  onSaved,
  onDone,
  onDecline,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [changing, setChanging] = useState(!saved);
  const [method, setMethod] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    if (!stripe || !elements) return;
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${tokenUrl}/setup`, {
        method_type: method,
        force_fresh: true,
        vault_only: true,
      });
      const result =
        method === "card"
          ? await stripe.confirmCardSetup(data.client_secret, {
              payment_method: { card: elements.getElement(CardElement) },
            })
          : await connectBank(stripe, data.client_secret, truckerName);
      if (result?.error) throw new Error(result.error.message);
      if (!result) {
        setBusy(false);
        return;
      }
      const synced = await axios.post(`${tokenUrl}/payment-method/sync`);
      onSaved(synced.data);
      setChanging(false);
      setMethod(null);
    } catch (err) {
      setError(
        errorMessage(err, err?.message || "We couldn't save that. Try again."),
      );
    }
    setBusy(false);
  }

  const showSaved = saved && !changing;
  const action = showSaved
    ? { label: "Continue", onClick: onDone }
    : method === "card"
      ? { label: "Save card", onClick: save, busy, busyLabel: "Saving…" }
      : method === "ach"
        ? {
            label: "Connect my bank",
            onClick: save,
            busy,
            busyLabel: "Connecting…",
          }
        : { label: "Continue", disabled: true };

  return (
    <StepShell
      question="How should we collect our fee?"
      onDecline={onDecline}
      action={action}
    >
      <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-emerald-900 short:py-2.5">
        <span
          aria-hidden="true"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-700 text-white"
        >
          ✓
        </span>
        <span className="font-[Barlow_Condensed] text-[19px] font-semibold leading-tight short:text-[17px]">
          We will not charge you without your consent.
        </span>
      </div>
      {freeLoad && (
        <p className="m-0 font-[Roboto] text-[15px] text-slate-600">
          This load is free, so it won't be charged. We keep your method for
          future loads.
        </p>
      )}

      {showSaved ? (
        <>
          <div className="grid grid-cols-[44px_minmax(0,1fr)_28px] items-center gap-3 rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-4 motion-safe:animate-bi-rise">
            <span
              aria-hidden="true"
              className="grid h-11 w-11 place-items-center rounded-xl bg-white text-xl"
            >
              {saved.payment_method_type === "ach" ? "🏦" : "💳"}
            </span>
            <span className="min-w-0 font-[Barlow_Condensed] text-[20px] font-bold leading-tight text-slate-900">
              {methodLabel(saved)}
            </span>
            <span
              aria-hidden="true"
              className="grid h-7 w-7 place-items-center rounded-full bg-emerald-700 font-bold text-white motion-safe:animate-bi-pop"
            >
              ✓
            </span>
          </div>
          <button
            type="button"
            onClick={() => setChanging(true)}
            className={`${textButton} self-center`}
          >
            Use a different payment method
          </button>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-2.5">
            <Option
              selected={method === "ach"}
              onClick={() => setMethod("ach")}
              glyph="🏦"
              title="Bank account"
              badge="Lower fees"
              note="Log in to your bank with Stripe"
            />
            <Option
              selected={method === "card"}
              onClick={() => setMethod("card")}
              glyph="💳"
              title="Debit or credit card"
              note="Visa, Mastercard, Amex, Discover"
            />
          </div>

          {method === "card" && (
            <div className="rounded-2xl border-2 border-slate-300 bg-white px-4 py-4 motion-safe:animate-bi-rise">
              <CardElement
                options={{
                  hidePostalCode: false,
                  style: { base: { fontSize: "18px", color: "#0f172a" } },
                }}
              />
            </div>
          )}
          {method === "ach" && (
            <p className="m-0 font-[Roboto] text-[12.5px] leading-snug text-slate-500">
              {NACHA_MANDATE_TEXT}
            </p>
          )}
          {saved && (
            <button
              type="button"
              onClick={() => setChanging(false)}
              className={`${textButton} self-center`}
            >
              Keep {methodLabel(saved)}
            </button>
          )}
        </>
      )}

      {(error || notice) && (
        <p className="m-0 font-[Roboto] text-[15px] text-red-700">
          {error || notice}
        </p>
      )}
    </StepShell>
  );
}

// Stripe's bank login, then confirm. Null when the carrier closes the window.
async function connectBank(stripe, clientSecret, name) {
  const collected = await stripe.collectBankAccountForSetup({
    clientSecret,
    params: {
      payment_method_type: "us_bank_account",
      payment_method_data: { billing_details: { name: name || "Trucker" } },
    },
  });
  if (collected.error) return collected;
  if (collected.setupIntent?.status === "requires_payment_method") return null;
  return stripe.confirmUsBankAccountSetup(clientSecret);
}

function Option({ selected, onClick, glyph, title, badge, note }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`${tileButton} grid grid-cols-[44px_minmax(0,1fr)_24px] items-center gap-3 !border-2 !border-solid !text-slate-900 short:!py-2.5 ${
        selected
          ? "!border-indigo-600 !bg-indigo-50"
          : "!border-slate-200 !bg-white hover:!bg-slate-50"
      }`}
    >
      <span
        aria-hidden="true"
        className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100 text-xl"
      >
        {glyph}
      </span>
      <span className="min-w-0">
        <span className="block text-[20px] font-bold leading-tight">
          {title}
          {badge && (
            <span className="ml-2 inline-block rounded-md bg-emerald-50 px-1.5 align-[3px] font-[Roboto] text-[11px] font-bold uppercase tracking-wide text-emerald-700">
              {badge}
            </span>
          )}
        </span>
        <span className="block font-[Roboto] text-[13.5px] font-normal text-slate-500">
          {note}
        </span>
      </span>
      <span
        aria-hidden="true"
        className={`h-[22px] w-[22px] rounded-full ${
          selected
            ? "border-[7px] border-indigo-600"
            : "border-2 border-slate-300"
        }`}
      />
    </button>
  );
}
